import { ChangeEvent, useState, useRef } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";

import axios from "axios";
import { toast } from "react-toastify";
import { GrEdit } from "react-icons/gr";
import { LuTrash } from "react-icons/lu";

import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import Spinner, { SpinnerV1 } from "~/components/themed/spinner";
import { Button } from "~/components/v3/themed/Button";
import { Label } from "~/components/v3/shadcn/Label";

import { Input } from "../../themed/Input";
import { TextArea } from "../../themed/TextArea";
import { Avatar } from "../../shadcn/Avatar";

import {
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "../../shadcn/Form";
import { CommunityType } from "../_types/CommunityTypes";
import { useCommunityURLChecker } from "~/hooks/useCommunityURLChecker";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";

export const CreateCommunityStep1 = () => {
    const { setValue, watch, control, formState, setError, clearErrors } =
        useFormContext<CommunityType>();
    const { touchedFields } = formState;

    const [isCheckingSlug, setIsCheckingSlug] = useState(false);
    const { isCommunityURLTaken } = useCommunityURLChecker();
    const nameChangeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const slugChangeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const [isUpLoading, setIsUpLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const avatarImage = watch("avatarImage");
    const communityName = watch("communityName")
        ?.replace(" ", "-")
        ?.toLowerCase();

    const { mutateAsync: fetchPresignedUrls } =
        api.s3.getStandardUploadPresignedUrl.useMutation();

    const socialLinks = useFieldArray({
        control: control,
        name: "socialLinks",
        rules: {
            minLength: 0,
            maxLength: 4,
        },
    });

    /** update slug/url if field is untouched */
    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (touchedFields?.communitySlug) return;
        const formattedSlug = e.target.value.toLowerCase().replace(/\s+/g, "-");
        setValue("communitySlug", formattedSlug);

        if (nameChangeTimeoutRef.current) {
            clearTimeout(nameChangeTimeoutRef.current);
        }

        nameChangeTimeoutRef.current = setTimeout(async () => {
            if (!formattedSlug) return;
            setIsCheckingSlug(true);
            try {
                const isTaken = await isCommunityURLTaken(formattedSlug);
                if (isTaken) {
                    setError("communitySlug", {
                        type: "manual",
                        message: "This URL is already taken",
                    });
                    toast.error("Community URL is already taken");
                } else {
                    clearErrors("communitySlug");
                }
            } catch (error) {
                console.error("Error checking URL:", error);
            } finally {
                setIsCheckingSlug(false);
            }
        }, 1000);
    };

    const handleSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (slugChangeTimeoutRef.current) {
            clearTimeout(slugChangeTimeoutRef.current);
        }

        slugChangeTimeoutRef.current = setTimeout(async () => {
            const slug = e.target.value;
            if (!slug) return;
            setIsCheckingSlug(true);
            try {
                const isTaken = await isCommunityURLTaken(slug);
                if (isTaken) {
                    setError("communitySlug", {
                        type: "manual",
                        message: "This URL is already taken",
                    });
                    toast.error("Community URL is already taken");
                } else {
                    clearErrors("communitySlug");
                }
            } catch (error) {
                console.error("Error checking URL:", error);
            } finally {
                setIsCheckingSlug(false);
            }
        }, 1000);
    };

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;

        const toastId = toast.loading(`Uploading Avatar image...`, {
            toastId: "upload-picture",
        });

        setIsUpLoading(true);
        const file = e.target.files[0];
        const ext = file.name.substring(file.name.lastIndexOf("."));
        const uploadFilename = `${(Math.random() + 1)
            .toString(36)
            .substring(7)}${ext}`;

        const presignedURL = await fetchPresignedUrls({
            key: `community/${uploadFilename}`,
        });

        let newFile = new File([file], uploadFilename, {
            type: file.type,
        });

        try {
            await axios.put(presignedURL, newFile.slice(), {
                headers: { "Content-Type": newFile.type },
            });

            toast.update(toastId, {
                type: "success",
                render: "Avatar Image successfully uploaded",
                isLoading: false,
                autoClose: 1200,
            });

            setValue(`avatarImage`, uploadFilename);
        } catch (error) {
            toast.update(toastId, {
                type: "error",
                render: "Error uploading profile picture!",
                isLoading: false,
                autoClose: 1200,
            });
            console.error(`Error updating profile picture`, error);
        }

        setIsUpLoading(false);
    };

    return (
        <div className=" flex flex-col gap-3">
            <div className="flex flex-col gap-2">
                <div>
                    <Avatar className="relative h-[6.5rem] w-[6.5rem] overflow-visible border-2 border-primary transition-[border-color] duration-300 group-hover:border-secondary">
                        {!isUpLoading ? (
                            <CommunityAvatar
                                community={{
                                    id: "test-id",
                                    name: communityName,
                                    image: avatarImage,
                                }}
                                className="h-full w-full"
                            />
                        ) : (
                            <div className="flex w-full items-center justify-center">
                                <Spinner className="h-5 w-5" />
                            </div>
                        )}
                        <button
                            type="button"
                            className={cn(
                                "absolute bottom-0 right-[-5px] z-50 cursor-pointer rounded-[0.6rem] bg-v3-N-450 p-2",
                                "active:scale-95",
                                {
                                    "cursor-not-allowed": isUpLoading,
                                },
                            )}
                            onClick={() => {
                                if (isUpLoading) return;
                                fileInputRef?.current?.click();
                            }}
                            disabled={isUpLoading}
                        >
                            <GrEdit className="text-xs text-white" />
                        </button>
                    </Avatar>
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => {
                            handleFileChange(e);
                        }}
                        accept="image/png, image/gif, image/jpeg, image/webp"
                        hidden
                    />
                </div>
            </div>

            <FormField
                control={control}
                name="communityName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                            Name your community on River*
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    handleNameChange(e);
                                }}
                                className="rounded-[0.626rem] tracking-wide"
                                placeholder="Community Name"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="communitySlug"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                            Set your community URL*
                        </FormLabel>
                        <FormControl>
                            <div className="relative">
                                <Input
                                    {...field}
                                    value={field.value ?? communityName}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        handleSlugChange(e);
                                    }}
                                    className="col-span-2 rounded-[0.626rem] pl-0 tracking-wide"
                                    placeholder="CommunityName"
                                    prefixComponent={
                                        <span>https://app.getriver.io/</span>
                                    }
                                />
                                {isCheckingSlug && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <SpinnerV1 className="h-4 w-4" />
                                    </div>
                                )}
                            </div>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="communityWebsite"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                            Community Website
                        </FormLabel>
                        <FormControl>
                            <Input
                                {...field}
                                className="rounded-[0.626rem] tracking-wide"
                                placeholder="Enter the Community Website URL"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={control}
                name="communityEventDescriptions"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                            Describe your community events* (You can edit this
                            later)
                        </FormLabel>
                        <FormControl>
                            <TextArea
                                {...field}
                                value={field.value ?? ""}
                                className="h-20 w-full rounded-[0.626rem]"
                            ></TextArea>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="mt-4 flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <Label className="text-sm font-normal tracking-wide text-[#A3A8B4]">
                        Social Links (e.g youtube, instagram etc)
                    </Label>
                </div>
                <div className="flex flex-col gap-4">
                    {socialLinks.fields?.map((socialLink, idx) => (
                        <div className="flex items-center gap-2" key={idx}>
                            <FormField
                                control={control}
                                name={`socialLinks.${idx}.url`}
                                key={socialLink.id}
                                render={({ field: { value, ...field } }) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={value ?? ""}
                                                className="rounded-[0.626rem] tracking-wide"
                                                placeholder="https://youtube.com/my-community"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <Button
                                onClick={(e) => {
                                    e.preventDefault();
                                    socialLinks.remove(idx);
                                }}
                                className="h-full rounded-[0.626rem]"
                                variant="destructive"
                            >
                                <LuTrash className="text-white" />
                            </Button>
                        </div>
                    ))}
                </div>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        socialLinks.append({ url: "" });
                    }}
                    className={cn(
                        "w-full rounded-[0.626rem]",
                        socialLinks.fields.length >= 4 &&
                            "cursor-not-allowed opacity-50",
                    )}
                    variant="secondary"
                    disabled={socialLinks.fields.length >= 4}
                >
                    Add social link
                </Button>
            </div>
        </div>
    );
};
